import { toast } from 'sonner';
import { captureException } from '@sentry/react';

import type { IntakeStatusBaseSteps } from '@shared/patient-intake-status';

import { toggleBaseStepVisibility } from './toggleBaseStepVisibility.ts';

type ToggleBaseStepVisibilityWithToastParams<TStep extends keyof IntakeStatusBaseSteps> = Parameters<
  typeof toggleBaseStepVisibility<TStep>
>[0] & {
  context: string;
};

export const toggleBaseStepVisibilityWithToast = <TStep extends keyof IntakeStatusBaseSteps>({
  step,
  stepId,
  context,
  patientId,
  actionedBy,
  isCurrentlyDisabled,
}: ToggleBaseStepVisibilityWithToastParams<TStep>) =>
  toast.promise(
    toggleBaseStepVisibility({
      step,
      stepId,
      patientId,
      actionedBy,
      isCurrentlyDisabled,
    }),
    {
      loading: isCurrentlyDisabled ? 'Enabling step...' : 'Disabling step...',
      success: {
        message: isCurrentlyDisabled ? 'Step is now enabled' : 'Step is now disabled',
        description: `The "${context}" step is now ${isCurrentlyDisabled ? 'enabled' : 'disabled'} for the patient.`,
        action: {
          label: 'Undo',
          onClick: () => {
            toggleBaseStepVisibility({
              step,
              stepId,
              patientId,
              actionedBy,
              isCurrentlyDisabled: !isCurrentlyDisabled,
            });
          },
        },
      },
      error: (error) => {
        if (import.meta.env.DEV) {
          console.error('Failed to toggle step visibility', {
            step,
            error,
            stepId,
            context,
            patientId,
            isCurrentlyDisabled,
          });
        }

        captureException(error, {
          tags: { issueIn: 'toggleBaseStepVisibilityWithToast' },
          extra: { stepId, patientId, isCurrentlyDisabled, step, context },
        });

        return {
          message: 'Failed to toggle step visibility',
          description: error.message,
        };
      },
    },
  );
